import type { ReactNode } from 'react';
import { MARKS, BLOCKS } from '@contentful/rich-text-types';
import { useLayoutContext } from '@/components/Layout/LayoutProvider';
import { localizeInCityToken } from '@/lib/utils/localizeInCityToken';
import type { PLPHeroBannerData } from './types';
import { usePaidLandingPageProvider } from '@/app/ready/[...slug]/PaidLandingPageProvider';

const formatPhoneHref = (phone?: string | null) => {
  if (!phone) return '';
  return `tel:${phone.replace(/[^\d+]/g, '')}`;
};

export function usePLPHeroBannerViewModel(data: PLPHeroBannerData) {
  const { callRailPhone } = useLayoutContext();
  const localizationData = usePaidLandingPageProvider();
  const localizedCity = localizationData.locationData?.locality ?? null;
  const isLocationLoading = localizationData.isLocationLoading;
  const offerFields = data.offer?.fields;
  const hasAlternativeTitle = offerFields?.alternativeTitle?.content?.some((node) =>
    node.content?.some((child) => child.nodeType === 'text' && child.value?.trim()),
  );
  const hasAlternativeTeaser = offerFields?.alternativeTeaser?.content?.some((node) =>
    node.content?.some((child) => child.nodeType === 'text' && child.value?.trim()),
  );

  const offerHeroTitle = hasAlternativeTitle ? offerFields?.alternativeTitle : offerFields?.title;

  const availabilityBadge = offerFields?.availabilityBadge || 'Tech Available Today';

  const offerHeroTeaser = hasAlternativeTeaser
    ? offerFields?.alternativeTeaser
    : offerFields?.teaser;

  const offerCardData = offerFields
    ? {
        __typename: 'OfferCard' as const,
        title: offerFields.title ?? '',
        teaser: offerFields.teaser ?? '',
        details: offerFields.details ?? null,
        linkTitle: offerFields.linkTitle ?? 'Offer Details',
        brandList: offerFields.brandList ?? [],
        expirationDate: offerFields.expirationDate ?? null,

        alternativeTitle: offerFields.alternativeTitle ?? null,
        alternativeTeaser: offerFields.alternativeTeaser ?? null,
        availabilityBadge: offerFields.availabilityBadge ?? null,
      }
    : null;

  // Title classes
  const titleClasses =
    "text-white font-bold lg:text-[64px] leading-[110%] font-['Instrument_Sans'] text-[32px] mb-4 first-letter:uppercase";
  const showMobileTitle = data.layout === 'Forward';

  const titleRichTextData =
    data.title && typeof data.title !== 'string'
      ? {
          content: data.title,
          className: showMobileTitle ? titleClasses : `${titleClasses} hidden lg:block`,
          options: {
            renderMark: {
              [MARKS.BOLD]: (text: ReactNode) => (
                <strong className='text-plp-hero-highlighted-text'>{text}</strong>
              ),
            },
            renderNode: {
              [BLOCKS.PARAGRAPH]: (_node: unknown, children: ReactNode) => <h1>{children}</h1>,
            },
            renderText: (text: string) => localizeInCityToken(text, localizedCity),
          },
        }
      : null;

  const offerTitleRichTextData =
    offerHeroTitle && typeof offerHeroTitle !== 'string'
      ? {
          content: offerHeroTitle,
          className:
            "text-primary-500 font-bold leading-[110%] font-['Instrument_Sans'] text-[32px] mb-0!",
          options: {
            renderMark: {
              [MARKS.BOLD]: (text: ReactNode) => (
                <strong className='text-hero-text-highlight'>{text}</strong>
              ),
            },
            renderNode: {
              [BLOCKS.PARAGRAPH]: (_node: unknown, children: ReactNode) => <h2>{children}</h2>,
            },
            renderText: (text: string) => localizeInCityToken(text, localizedCity),
          },
        }
      : null;

  const offerTitleText =
    typeof offerHeroTitle === 'string' ? localizeInCityToken(offerHeroTitle, localizedCity) : null;

  const offerTeaserRichTextData =
    offerHeroTeaser && typeof offerHeroTeaser !== 'string'
      ? {
          content: offerHeroTeaser,
          className: 'font-medium text-[18px] leading-[160%]',
          options: {
            renderText: (text: string) => localizeInCityToken(text, localizedCity),
          },
        }
      : null;

  const offerTeaserText =
    typeof offerHeroTeaser === 'string'
      ? localizeInCityToken(offerHeroTeaser, localizedCity)
      : null;

  return {
    callRailPhone,
    phoneHref: formatPhoneHref(callRailPhone),
    availabilityBadge,
    titleRichTextData,
    copy: data.copy,
    offer: data.offer,
    offerTitleRichTextData,
    offerTeaserRichTextData,
    offerTeaser: offerFields?.teaser,
    offerDetails: offerFields?.details,
    primaryCtaLabel: data.primaryCtaLabelOverride || 'Book Now',
    secondaryCtaLabel: data.secondaryCtaLabelOverride || callRailPhone,
    localizedCity,
    isLocationLoading,
    offerCardData,
    offerTitleText,
    offerTeaserText,
    anchorId: data.anchorId
  };
}
